/**
 * Configuración de Navegación de la Aplicación
 *
 * Este archivo centraliza toda la estructura del menú sidebar,
 * facilitando el mantenimiento y la adición de nuevas secciones.
 */
import {
  FileText,
  Ship,
  Calculator,
  Users,
  Shield,
  Receipt,
  Upload,
  Database,
  Building,
} from "lucide-react";
export interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number; // Opcional: para mostrar badges/contadores
  description?: string; // Opcional: para tooltips
  disabled?: boolean; // Opcional: para deshabilitar el item
  comingSoon?: boolean; // Opcional: para marcar como "próximamente"
  hidden?: boolean; // Opcional: para ocultar el item del menú
}
export interface NavGroup {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
  collapsed?: boolean; // Opcional: estado inicial del grupo
}
/**
 * Items de navegación de nivel superior (fuera de grupos)
 */
export const topLevelItems: NavItem[] = [
  {
    title: "Empresas",
    href: "/empresas",
    icon: Building,
    description: "Resumen de empresas con totales de operaciones",
  },
  {
    title: "Operaciones",
    href: "/operaciones",
    icon: Ship,
    description: "Gestión de operaciones aduaneras",
  },
  {
    title: "Contratos",
    href: "/contratos",
    icon: FileText,
    description: "Gestión de contratos",
    hidden: true, // Oculto temporalmente
  },
  {
    title: "Tarifas",
    href: "/tarifas",
    icon: Calculator,
    description: "Tarifas",
    hidden: true, // Oculto temporalmente
  },
  {
    title: "Bookings",
    href: "/bookings",
    icon: Ship,
    description: "Gestión de bookings",
    hidden: true, // Oculto temporalmente
  },
  {
    title: "Facturas de Proveedores",
    href: "/finanzas/facturas",
    icon: FileText,
    description: "Facturas de proveedores",
    hidden: true, // Oculto temporalmente
  },
  {
    title: "Facturas Internas",
    href: "/finanzas/facturas-internas",
    icon: Receipt,
    description: "Facturas internas",
    hidden: true, // Oculto temporalmente
  },
  {
    title: "Importaciones",
    href: "/importaciones",
    icon: Upload,
    description: "Importar contratos y facturas desde Excel",
  },
  {
    title: "Maestros",
    href: "/maestros",
    icon: Database,
    description: "Gestión de maestros y nomencladores",
  },
];
/**
 * Grupos de navegación (secciones colapsables)
 */
export const navigationGroups: NavGroup[] = [
  {
    title: "Configuración",
    icon: Shield,
    items: [
      {
        title: "Usuarios",
        href: "/configuracion/usuarios",
        icon: Users,
        description: "Administración de usuarios",
      },
    ],
  },
  // Puedes añadir más grupos aquí:
  // {
  //   title: "Herramientas",
  //   icon: Settings,
  //   items: [
  //     { title: "...", href: "/...", icon: ... },
  //   ],
  // },
];
/**
 * Helper para obtener el item de navegación según la ruta actual
 */
export function getNavItemByPath(pathname: string): NavItem | undefined {
  // Buscar en items de nivel superior
  const topLevel = topLevelItems.find((item) => item.href === pathname);
  if (topLevel) return topLevel;
  // Buscar en grupos
  for (const group of navigationGroups) {
    const groupItem = group.items.find((item) => item.href === pathname);
    if (groupItem) return groupItem;
  }
  return undefined;
}
/**
 * Helper para verificar si una ruta está activa
 */
export function isNavItemActive(
  itemHref: string,
  currentPath: string,
): boolean {
  // Coincidencia exacta
  if (itemHref === currentPath) return true;
  // Coincidencia por prefijo (para rutas anidadas)
  if (currentPath.startsWith(itemHref) && itemHref !== "/") return true;
  return false;
}
export default {
  topLevelItems,
  navigationGroups,
  getNavItemByPath,
  isNavItemActive,
};
